'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiLoyaltyInformerCount } from '@/fo/ui/loyalty/informer/count';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiPaymentBnplBadge } from '@/fo/ui/payment/bnpl/badge';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventElementVisibility } from '@/fo/util/event-element-visibility';
import { utilGetHighBonusLabel } from '@/fo/util/get-high-bonus-label';
import { Option, MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './UiProductInfoMain.styles';

export const EVENT_NAME_PRODUCT_INFO_MAIN_SHOW = 'UiProductInfoMainShow';
export const EVENT_NAME_PRODUCT_INFO_MAIN_HIDE = 'UiProductInfoMainHide';

export interface UiProductInfoMainProps {
  type?: 'pdp' | 'catalog';
  isAuthorized: boolean;
  isInWishlist: boolean;
  isCartLoading: boolean;
  labels?: React.ReactNode;
  product: MappedProduct;
  maxStockQuantity: number;
  loyaltyLevel?: number;
  cartCount?: number;
  className?: string;
  onWishlistClick?: () => void;
  onCartClick?: () => void;
  onCartChange?: (quantity: number) => void;
  onOptionChange?: (product: MappedProduct) => void;
  goToDescription?: () => void;
}

export function UiProductInfoMain({
  type = 'pdp',
  isAuthorized,
  isInWishlist,
  isCartLoading,
  labels,
  product: initialProduct,
  maxStockQuantity,
  loyaltyLevel,
  cartCount = 0,
  className,
  onWishlistClick,
  onCartClick,
  onCartChange,
  onOptionChange,
  goToDescription
}: UiProductInfoMainProps) {
  const slots = styles({ className });
  const t = useTranslations();
  const isMobile = useMediaQuery('lg', 'max');

  const [product, setProduct] = useState(initialProduct);

  const [configurableVariants, setConfigurableVariants] = useState(
    initialProduct.configurableVariants
  );

  const buyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((newProduct) => {
      setConfigurableVariants((prev) =>
        prev?.map((option) => ({
          ...option,
          isSelected:
            newProduct.configurableVariants?.find(
              (item) => option.sku === item.sku
            )?.isSelected ?? false
        }))
      );

      setProduct(newProduct);

      onOptionChange?.(newProduct);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!buyButtonRef.current || type !== 'pdp') {
      return;
    }

    const getHeaderHeight = () => {
      const height = getComputedStyle(document.documentElement)
        .getPropertyValue('--head-fixed-height')
        .replace('px', '');
      return height ? parseInt(height, 10) : 0;
    };

    const updateVisibility = (isIntersecting: boolean, rect: DOMRect) => {
      if (isIntersecting) {
        utilEventElementVisibility.hide(EVENT_NAME_PRODUCT_INFO_MAIN_HIDE);
        return;
      }

      if (isMobile) {
        utilEventElementVisibility.show(EVENT_NAME_PRODUCT_INFO_MAIN_SHOW);
        return;
      }

      const isAboveBuyButton = rect.top > window.innerHeight;
      const isButtonPassed = rect.bottom < getHeaderHeight();

      if (isAboveBuyButton) {
        utilEventElementVisibility.hide(EVENT_NAME_PRODUCT_INFO_MAIN_HIDE);
        return;
      }

      if (isButtonPassed) {
        utilEventElementVisibility.show(EVENT_NAME_PRODUCT_INFO_MAIN_SHOW);
        return;
      }

      utilEventElementVisibility.hide(EVENT_NAME_PRODUCT_INFO_MAIN_HIDE);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const rect = entry.target.getBoundingClientRect();
        updateVisibility(entry.isIntersecting, rect);
      },
      {
        threshold: [0, 1]
      }
    );

    observer.observe(buyButtonRef.current);

    const rect = buyButtonRef.current.getBoundingClientRect();
    const isIntersecting = rect.bottom > 0 && rect.top < window.innerHeight;
    updateVisibility(isIntersecting, rect);

    return () => {
      observer.disconnect();
    };
  }, [type, isMobile]);

  const onOptionClick = (option: Option) => {
    const { product: newProduct } = option;

    if (!newProduct) {
      return;
    }

    newProduct.parentSku = product.parentSku;
    newProduct.parentVideo = product.parentVideo;

    utilEventConfigurableProduct.selectProduct(newProduct);

    setProduct(newProduct);

    setConfigurableVariants((prev) =>
      prev?.map((item) => ({
        ...item,
        isSelected: item.sku === newProduct.sku
      }))
    );

    onOptionChange?.(newProduct);
  };

  const highBonusLabel = utilGetHighBonusLabel(
    product.earnedBonusPoints,
    product.basePrice,
    loyaltyLevel,
    isAuthorized
  )
    ? [{ name: t('Increased cashback'), color: '' }]
    : [];

  return (
    <div className={slots.base({ className })}>
      <div className={slots.block()}>
        <div className={slots.info({ catalog: type === 'catalog' })}>
          {type === 'catalog' && product.thumbnail && (
            <Image
              className={slots.image({ catalog: type === 'catalog' })}
              src={product.thumbnail?.src}
              width={120}
              height={120}
              alt={product.thumbnail?.alt}
            />
          )}

          <div>
            {!!labels && (
              <div className={slots.labels({ catalog: type === 'catalog' })}>
                {labels}
              </div>
            )}

            <h1 className={slots.name({ catalog: type === 'catalog' })}>
              <span className="sr-only">{product.series} </span>
              <span
                className={slots.nameText({ catalog: type === 'catalog' })}
                dangerouslySetInnerHTML={{ __html: product.name }}
              />
              <span className="sr-only"> {product.brand} </span>
              {!!initialProduct.subtitle && (
                <span
                  className={slots.shortDescription({
                    catalog: type === 'catalog'
                  })}
                  dangerouslySetInnerHTML={{
                    __html: initialProduct.subtitle
                  }}
                  suppressHydrationWarning
                />
              )}
            </h1>
          </div>
        </div>

        {type === 'pdp' && (
          <Link
            className={slots.link()}
            href="#description"
            onClick={goToDescription}
          >
            {t('Go to the description')}
          </Link>
        )}
      </div>

      <div className={slots.block()}>
        {configurableVariants && (
          <UiProductOptionsBase
            className="mb-5"
            size="l"
            options={configurableVariants}
            onOptionClick={(option) =>
              option.isSelected ? null : onOptionClick(option)
            }
          />
        )}
        {product.stockStatus && (
          <div
            className={slots.stock({
              outOfStock: product.stockStatus === 'OUT_OF_STOCK'
            })}
          >
            {product.stockStatus === 'OUT_OF_STOCK'
              ? t('Out of stock')
              : t('In stock')}
          </div>
        )}
        {product.stockStatus === 'IN_STOCK' && (
          <>
            {product.basePrice !== null && product.basePrice > 0 && (
              <UiKitPriceBase
                value={product.finalPrice ?? product.basePrice}
                oldValue={product.finalPrice ? product.basePrice : null}
                className={slots.price()}
                classNameValue="heading-3xl"
                classNameOldValue="heading-m"
              />
            )}
            {product.bonusPointsMax > 0 && (
              <div className={slots.loyalty()}>
                <UiLoyaltyInformerCount
                  isAuthorized={isAuthorized}
                  bonuses={product.bonusPoints}
                  bonusesMax={product.bonusPointsMax}
                  label={[...product.labelsBonus, ...highBonusLabel][0]?.name}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div
        className={slots.actions()}
        data-mobile-app-hidden="commerce"
      >
        {cartCount > 0 && (
          <UiProductCounterInputBase
            onDebounceChange={onCartChange}
            value={cartCount}
            loading={isCartLoading && cartCount > 0}
            max={maxStockQuantity}
          />
        )}
        <UiKitButtonBase
          className={slots.addToCart()}
          onClick={onCartClick}
          ref={buyButtonRef}
          loading={isCartLoading && cartCount === 0}
        >
          {product.stockStatus === 'OUT_OF_STOCK' &&
            t('Find out about admission')}
          {product.stockStatus === 'IN_STOCK' && cartCount > 0 && t('In cart')}
          {product.stockStatus === 'IN_STOCK' &&
            cartCount === 0 &&
            t('To cart')}
        </UiKitButtonBase>
        <div className={slots.wishlist()}>
          <UiProductWishlistButton
            className={slots.wishlistIcon()}
            isActive={isInWishlist}
            onClick={onWishlistClick}
          />
        </div>
      </div>
      <UiPaymentBnplBadge
        amount={product.finalPrice ?? product.basePrice ?? 0}
        size="m"
        variant="detailed"
        color="transparent"
        theme="light"
        align="left"
      />
    </div>
  );
}
